import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "../assets/css/Tour.css";

// Guided onboarding tours (driver.js prototype). Steps target elements by
// data-tour="..." attributes so they don't break when classes or copy change.

const baseConfig = {
  popoverClass: "bizbrand-tour",
  showProgress: true,
  allowClose: true,
  overlayOpacity: 0.55,
  stagePadding: 6,
  stageRadius: 12,
  nextBtnText: "Next",
  prevBtnText: "Back",
};

// ---------------------------------------------------------------------------
// Career Success Index: intro screen, then the first question screen.
// The intro's last step starts the assessment; the form tour runs once the
// questions have loaded. It auto-runs once per user (see shouldAutoStartCsiTour).
// ---------------------------------------------------------------------------

const csiTourSeenKey = (userId) => `csi_tour_seen_${userId}`;

export const shouldAutoStartCsiTour = (userId) => {
  if (!userId) return false;
  try {
    return localStorage.getItem(csiTourSeenKey(userId)) !== "1";
  } catch {
    return false;
  }
};

export const markCsiTourSeen = (userId) => {
  if (!userId) return;
  try {
    localStorage.setItem(csiTourSeenKey(userId), "1");
  } catch {
    /* storage unavailable: the tour may show again, which is harmless */
  }
};

// First-visit welcome: a centered card (no element) that says what the CSI is
// and asks before touring, so the walkthrough isn't the first thing they see.
const csiWelcomeStep = (getTour) => ({
  popover: {
    title: "Welcome to your Career Success Index",
    description: `
      <p>A short self-check on where your career is strong today, and where to grow next.</p>
      <ul>
        <li><strong>About 10 minutes</strong>: 50 quick statements in 5 areas</li>
        <li>No right or wrong answers. Just answer honestly</li>
        <li>You'll get your score and a personal coaching report at the end</li>
      </ul>
      <p>Want a quick look at how it works before you start?</p>`,
    popoverClass: "bizbrand-tour bizbrand-tour-welcome",
    showProgress: false,
    showButtons: ["next", "close"],
    nextBtnText: "Show me around",
    onPopoverRender: (popover) => {
      const skip = document.createElement("button");
      skip.type = "button";
      skip.textContent = "Skip for now";
      skip.className = "driver-popover-footer-btn bizbrand-tour-skip-btn";
      skip.addEventListener("click", () => getTour().destroy());
      popover.footerButtons.insertBefore(skip, popover.nextButton);
    },
  },
});

export const startCsiIntroTour = ({ onStart, welcome = false }) => {
  const guided = [
    {
      element: '[data-tour="csi-categories"]',
      popover: {
        title: "Five quick categories",
        description: "10 short questions in each. Most people finish a category in a couple of minutes.",
        side: "top",
        align: "start",
      },
    },
    {
      element: '[data-tour="csi-scale"]',
      popover: {
        title: "How you'll answer",
        description: "Rate each statement from Strongly Disagree to Strongly Agree. Pick N/A if it doesn't apply to you; it won't count toward your score.",
        side: "top",
        align: "start",
      },
    },
    {
      element: '[data-tour="csi-start"]',
      popover: {
        title: "Ready when you are",
        description: "Your answers are only submitted at the end, after a Review step.",
        side: "top",
        align: "center",
        doneBtnText: "Start assessment",
        onNextClick: () => {
          tour.destroy();
          onStart();
        },
      },
    },
  ];
  // Count progress over the guided steps only, so the welcome card isn't "1 of 4"
  guided.forEach((step, i) => {
    step.popover.progressText = `${i + 1} of ${guided.length}`;
  });

  const tour = driver({
    ...baseConfig,
    steps: welcome ? [csiWelcomeStep(() => tour), ...guided] : guided,
  });
  tour.drive();
  return tour;
};

export const startCsiFormTour = () => {
  const tour = driver({
    ...baseConfig,
    steps: [
      {
        element: '[data-tour="csi-progress"]',
        popover: {
          title: "Your progress",
          description: "One step per category, then Review. Finished steps get a check, and you can click them to go back.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: '[data-tour="csi-first-question"]',
        popover: {
          title: "Answer with the slider",
          description: "Click a point on the line (or use the arrow keys). Use N/A on the right if the statement doesn't apply. Try it now.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: '[data-tour="csi-nav"]',
        popover: {
          title: "Moving on",
          description: "Next unlocks once every question on the page has an answer. The counter shows how many are left.",
          side: "top",
          align: "end",
          doneBtnText: "Got it",
        },
      },
    ],
  });
  tour.drive();
  return tour;
};

// ---------------------------------------------------------------------------
// "Connect LinkedIn": spans two pages. The dashboard part ends by sending the
// user to /preferences?tour=linkedin, and MyPreferences picks the tour back up
// there, since a driver.js tour can't survive a route change on its own.
// ---------------------------------------------------------------------------

export const LINKEDIN_TOUR_QUERY = "tour=linkedin";

// Onboarding task ids differ between dev and prod, so match by text
// (same approach as handleCompleteOnboardingClick on the dashboard).
export const isLinkedInOnboardingTask = (task) =>
  `${task?.title || ""} ${task?.note || ""}`.toLowerCase().includes("linkedin");

export const startLinkedInDashboardTour = ({ onGoToPreferences }) => {
  const tour = driver({
    ...baseConfig,
    steps: [
      {
        element: '[data-tour="onboarding-tasks"]',
        popover: {
          title: "Your onboarding checklist",
          description: "These steps set up your profile. Let's knock out connecting LinkedIn.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: '[data-tour="onboarding-linkedin-task"]',
        popover: {
          title: "Connect LinkedIn",
          description: "This task is done once your LinkedIn profile URL is saved on your account.",
          side: "bottom",
          align: "start",
          doneBtnText: "Take me there",
          onNextClick: () => {
            tour.destroy();
            onGoToPreferences();
          },
        },
      },
    ],
  });
  tour.drive();
  return tour;
};

export const startLinkedInPreferencesTour = ({ needsPosition, onFinish }) => {
  const tour = driver({
    ...baseConfig,
    onDestroyed: onFinish,
    steps: [
      {
        element: '[data-tour="linkedin-url"]',
        popover: {
          title: "Paste your LinkedIn URL",
          description: "Copy it from your LinkedIn profile, e.g. https://linkedin.com/in/your-name. You can type in the field while this is open.",
          side: "top",
          align: "start",
        },
      },
      {
        element: '[data-tour="preferences-save"]',
        popover: {
          title: "Save to finish",
          description: needsPosition
            ? "Pick at least one Target Position first (Save stays disabled until you do), then click Save Changes to complete the task."
            : "Click Save Changes and your \"Connect LinkedIn\" onboarding task is marked complete.",
          side: "top",
          align: "start",
          doneBtnText: "Got it",
        },
      },
    ],
  });
  tour.drive();
  return tour;
};
